/**
 * Rain and Blizzard on screen (DESIGN 2.9 global Magicks). Both cover the whole map while they
 * last, so the play area shows it, not only the timer badge by the room panel:
 * - Rain: slanted drops falling onto the floor in view, with a splash where each lands; puddles
 *   gather on open floor while it pours (rings shiver in them) and dry after; a cool blue tint.
 * - Blizzard: pixel snow drifting down on the wind; frost creeping over open floor the longer it
 *   blows (puddles still lying there ice over); a pale tint with frosted screen edges.
 *
 * Purely visual: client randomness and timers, nothing the simulation reads, so the seeded
 * waves replay is untouched. Particle counts follow the view's area and are capped lower with
 * touch controls (a phone) and with reduced motion. Drops and flakes live in world space (a
 * ground point and a height in px), so they stay put on the floor while the camera moves.
 */

import type { Arena } from '@shared/index.js';
import { cellAt, inWaterZone } from '@shared/index.js';
import { GROUND } from '../palette.js';
import { TAU, type Ctx } from './gfx.js';
import type { Camera } from './camera.js';

/** Seconds a weather takes to fade in once it starts, and to fade out over its last seconds. */
export const FADE_IN_S = 0.6, FADE_OUT_S = 1.2;
/** Seconds of rain to fill the puddles, of dry weather to dry them; the same for frost. */
const PUDDLE_FILL_S = 3, PUDDLE_DRY_S = 6, FROST_GROW_S = 4, FROST_MELT_S = 3;
/** Particles per 10 000 CSS px of view, and the most there may be (desktop, then touch). */
const RAIN_DENSITY = 3, SNOW_DENSITY = 2;
const RAIN_CAP = 280, SNOW_CAP = 200, RAIN_CAP_TOUCH = 90, SNOW_CAP_TOUCH = 70;
/** How high (px) a drop or flake starts above its ground point, and how far the wind slants it per px of fall. */
const RAIN_Z = 260, SNOW_Z = 300, RAIN_SLANT = 0.22, SNOW_SLANT = 0.12;
const SPLASH_CAP = 96, SPLASH_S = 0.22, SETTLE_S = 0.6;
/** Puddle and frost patches: one candidate per CELL x CELL units of map. */
const CELL = 3;

/** 0..1 strength of a weather with `left` seconds to go that has been on screen for `since` seconds. */
export function weatherLevel(left: number, since: number): number {
  if (!(left > 0)) return 0;
  return Math.min(1, Math.max(0, since) / FADE_IN_S) * Math.min(1, left / FADE_OUT_S);
}

/** How many drops and flakes a view of `w` x `h` CSS px gets at full strength. */
export function particleBudget(w: number, h: number, touch: boolean, reduceMotion = false): { rain: number; snow: number } {
  const area = Math.max(0, w) * Math.max(0, h) / 10_000;
  const k = reduceMotion ? 0.5 : 1;
  return {
    rain: Math.round(Math.min(touch ? RAIN_CAP_TOUCH : RAIN_CAP, area * RAIN_DENSITY) * k),
    snow: Math.round(Math.min(touch ? SNOW_CAP_TOUCH : SNOW_CAP, area * SNOW_DENSITY) * k),
  };
}

/** A puddle or frost patch on the floor: centre and radii in world units, and a 0..1 seed. */
export interface Patch { x: number; y: number; rx: number; ry: number; seed: number }

/** A cheap, stable 0..1 hash of a cell and a salt. */
function hash01(x: number, y: number, salt: number): number {
  let h = Math.imul(x, 374761393) ^ Math.imul(y, 668265263) ^ Math.imul(salt, 2246822519);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

/**
 * Open floor at (x, y): on the map, not in a wall, a pillar, standing water, deep water, a
 * chasm or a bramble cell. Puddles and frost only lie where feet could stand.
 */
export function openFloor(a: Arena, x: number, y: number, blocked: ReadonlySet<number>): boolean {
  if (x < 0.4 || y < 0.4 || x > a.w - 0.4 || y > a.h - 0.4) return false;
  for (const b of a.walls) if (x >= b.x - 0.2 && x <= b.x + b.w + 0.2 && y >= b.y - 0.2 && y <= b.y + b.h + 0.2) return false;
  for (const p of a.pillars) if (Math.hypot(p.x - x, p.y - y) < p.r + 0.5) return false;
  for (const z of a.water) if (inWaterZone({ x, y }, z)) return false;
  return !blocked.has(cellAt(a, { x, y }));
}

/**
 * Where puddles (`salt` 1) or frost (`salt` 2) lie on a map: at most one per CELL-unit cell,
 * placed by a stable hash, each wholly on open floor. The same map always gets the same spots.
 */
export function floorPatches(a: Arena, salt: 1 | 2): Patch[] {
  const blocked = new Set<number>([...a.deepCells, ...a.pitCells, ...a.brambleCells]);
  const odds = salt === 1 ? 0.45 : 0.6;
  const out: Patch[] = [];
  for (let cy = 0; cy * CELL < a.h; cy++) {
    for (let cx = 0; cx * CELL < a.w; cx++) {
      if (hash01(cx, cy, salt) >= odds) continue;
      const x = cx * CELL + 0.6 + hash01(cx, cy, salt + 10) * (CELL - 1.2);
      const y = cy * CELL + 0.6 + hash01(cx, cy, salt + 20) * (CELL - 1.2);
      const rx = salt === 1 ? 0.4 + hash01(cx, cy, salt + 30) * 0.6 : 0.7 + hash01(cx, cy, salt + 30) * 0.8;
      const ry = rx * 0.45;
      if (!openFloor(a, x, y, blocked) || !openFloor(a, x - rx, y, blocked) || !openFloor(a, x + rx, y, blocked)
        || !openFloor(a, x, y - ry, blocked) || !openFloor(a, x, y + ry, blocked)) continue;
      out.push({ x, y, rx, ry, seed: hash01(cx, cy, salt + 40) });
    }
  }
  return out;
}

interface Drop { x: number; y: number; z: number; v: number; ph: number; settle: number }
interface Splash { x: number; y: number; t: number }

export class Weather {
  /** 0..1 strength of each, after the fades. */
  rain = 0;
  snow = 0;
  /** 0..1: how full the puddles are, how far the frost has spread. */
  wet = 0;
  frost = 0;
  touch = false;
  reduceMotion = false;

  private rainSince = 0;
  private snowSince = 0;
  private readonly drops: Drop[] = [];
  private readonly flakes: Drop[] = [];
  private readonly splashes: Splash[] = [];
  private splashNext = 0;
  private budget = { rain: 0, snow: 0 };
  private arenaId = '';
  private puddles: Patch[] = [];
  private frostPatches: Patch[] = [];
  private time = 0;
  private edgeKey = '';
  private edge: CanvasGradient | null = null;

  /** Drops and flakes in play right now (tests). */
  get counts(): { rain: number; snow: number; splashes: number } {
    return { rain: this.activeRain(), snow: this.activeSnow(), splashes: this.splashes.filter((s) => s.t < SPLASH_S).length };
  }

  /** Puddles and frost follow the map (a new level gets its own spots). */
  setArena(a: Arena): void {
    if (a.id === this.arenaId) return;
    this.arenaId = a.id;
    this.puddles = floorPatches(a, 1);
    this.frostPatches = floorPatches(a, 2);
  }

  /** Advance by `dt` s with `rainLeft` / `snowLeft` seconds of each still to go (WaveSnap rain / blizzard). */
  step(dt: number, rainLeft: number, snowLeft: number, cam: Camera): void {
    this.time += dt;
    this.rainSince = rainLeft > 0 ? this.rainSince + dt : 0;
    this.snowSince = snowLeft > 0 ? this.snowSince + dt : 0;
    this.rain = weatherLevel(rainLeft, this.rainSince);
    this.snow = weatherLevel(snowLeft, this.snowSince);
    this.wet = this.rain > 0 ? Math.min(1, this.wet + dt / PUDDLE_FILL_S) : Math.max(0, this.wet - dt / PUDDLE_DRY_S);
    this.frost = this.snow > 0 ? Math.min(1, this.frost + dt / FROST_GROW_S) : Math.max(0, this.frost - dt / FROST_MELT_S);
    this.budget = particleBudget(cam.viewW, cam.viewH, this.touch, this.reduceMotion);
    if (this.rain > 0) this.stepRain(dt, cam);
    else this.drops.length = 0;
    if (this.snow > 0) this.stepSnow(dt, cam);
    else this.flakes.length = 0;
    for (const s of this.splashes) s.t += dt;
  }

  private activeRain(): number { return Math.min(this.drops.length, Math.round(this.budget.rain * this.rain)); }
  private activeSnow(): number { return Math.min(this.flakes.length, Math.round(this.budget.snow * this.snow)); }

  private stepRain(dt: number, cam: Camera): void {
    while (this.drops.length < this.budget.rain) this.drops.push(this.spawn(cam, RAIN_Z, RAIN_SLANT, true));
    this.drops.length = this.budget.rain;
    const n = this.activeRain();
    for (let i = 0; i < n; i++) {
      const d = this.drops[i]!;
      d.z -= d.v * dt;
      if (d.z > 0 && this.inView(d, cam, RAIN_Z)) continue;
      if (d.z <= 0 && i % 2 === 0) {
        const s = this.splashes[this.splashNext];
        if (s) { s.x = d.x; s.y = d.y; s.t = 0; } else this.splashes.push({ x: d.x, y: d.y, t: 0 });
        this.splashNext = (this.splashNext + 1) % SPLASH_CAP;
      }
      this.respawn(d, cam, RAIN_Z, RAIN_SLANT, false);
    }
  }

  private stepSnow(dt: number, cam: Camera): void {
    while (this.flakes.length < this.budget.snow) this.flakes.push(this.spawn(cam, SNOW_Z, SNOW_SLANT, true));
    this.flakes.length = this.budget.snow;
    const n = this.activeSnow();
    for (let i = 0; i < n; i++) {
      const f = this.flakes[i]!;
      if (f.z > 0) {
        f.z -= f.v * dt;
        if (f.z <= 0) { f.z = 0; f.settle = SETTLE_S; }
        if (this.inView(f, cam, SNOW_Z)) continue;
      } else {
        // a landed flake lies a moment, then melts away
        f.settle -= dt;
        if (f.settle > 0 && this.inView(f, cam, SNOW_Z)) continue;
      }
      this.respawn(f, cam, SNOW_Z, SNOW_SLANT, false);
    }
  }

  /** The ground box whose drops can show on screen: the view, widened by how far a fall slants and rises. */
  private spawn(cam: Camera, zMax: number, slant: number, anyHeight: boolean): Drop {
    const d: Drop = { x: 0, y: 0, z: 0, v: 0, ph: 0, settle: 0 };
    this.respawn(d, cam, zMax, slant, anyHeight);
    return d;
  }

  private respawn(d: Drop, cam: Camera, zMax: number, slant: number, anyHeight: boolean): void {
    const rise = zMax / cam.scale;
    d.x = cam.left + Math.random() * (cam.right - cam.left + rise * slant);
    d.y = cam.top + Math.random() * (cam.bottom - cam.top + rise);
    d.z = anyHeight ? Math.random() * zMax : zMax * (0.75 + Math.random() * 0.25);
    d.v = zMax === RAIN_Z ? 760 + Math.random() * 260 : 45 + Math.random() * 40;
    d.ph = Math.random() * TAU;
    d.settle = 0;
  }

  private inView(d: Drop, cam: Camera, zMax: number): boolean {
    const rise = zMax / cam.scale;
    return d.x >= cam.left - 1 && d.x <= cam.right + rise && d.y >= cam.top - 1 && d.y <= cam.bottom + rise;
  }

  // ---------------------------------------------------------------- drawing

  /** Layer 1-2: puddles and frost on the open floor, under everything that stands. */
  drawGround(ctx: Ctx, cam: Camera): void {
    if (this.wet <= 0 && this.frost <= 0) return;
    const l = cam.left - 2, r = cam.right + 2, t = cam.top - 1, b = cam.bottom + 1;
    const s = cam.scale;
    if (this.wet > 0) {
      for (const p of this.puddles) {
        if (p.x < l || p.x > r || p.y < t || p.y > b) continue;
        // puddles fill one by one, the big ones last
        const fill = Math.min(1, Math.max(0, this.wet * 1.6 - p.seed * 0.6));
        if (fill <= 0) continue;
        const cx = Math.round(cam.sx(p.x)), cy = Math.round(cam.sy(p.y));
        const rx = p.rx * s * (0.4 + 0.6 * fill), ry = p.ry * s * (0.4 + 0.6 * fill);
        ctx.globalAlpha = fill * 0.55;
        ctx.fillStyle = this.frost > 0.4 ? GROUND.pondFrozen : GROUND.pond;
        ctx.beginPath();
        ctx.ellipse(cx, cy, rx, ry, 0, 0, TAU);
        ctx.fill();
        ctx.globalAlpha = fill * 0.5;
        ctx.strokeStyle = 'rgba(160,205,255,0.8)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.ellipse(cx - rx * 0.25, cy - ry * 0.3, rx * 0.45, ry * 0.3, 0, Math.PI * 1.1, Math.PI * 1.9);
        ctx.stroke();
        if (this.rain > 0 && this.frost <= 0.4) {
          // rain lands in it: a ring grows and fades
          const f = (this.time * 1.4 + p.seed * 7) % 1;
          ctx.globalAlpha = (1 - f) * 0.6 * this.rain * fill;
          ctx.beginPath();
          ctx.ellipse(cx + (p.seed - 0.5) * rx, cy, rx * 0.15 + rx * 0.5 * f, ry * 0.15 + ry * 0.5 * f, 0, 0, TAU);
          ctx.stroke();
        }
      }
    }
    if (this.frost > 0) {
      for (const p of this.frostPatches) {
        if (p.x < l || p.x > r || p.y < t || p.y > b) continue;
        const grow = Math.min(1, Math.max(0, this.frost * 1.5 - p.seed * 0.5));
        if (grow <= 0) continue;
        const cx = Math.round(cam.sx(p.x)), cy = Math.round(cam.sy(p.y));
        const rx = p.rx * s * grow, ry = p.ry * s * grow;
        // three overlapping lobes, so a patch reads as frost creeping out, not a disc
        ctx.fillStyle = '#E8F6FF';
        for (let k = 0; k < 3; k++) {
          const a = p.seed * 20 + k * 2.1;
          const ox = Math.cos(a) * rx * 0.45, oy = Math.sin(a) * ry * 0.45;
          ctx.globalAlpha = grow * 0.11;
          ctx.beginPath();
          ctx.ellipse(cx + ox, cy + oy, rx * (0.55 + 0.15 * k), ry * (0.55 + 0.15 * k), 0, 0, TAU);
          ctx.fill();
        }
        // crystals in the cast's pixel size, thickest in the middle
        ctx.fillStyle = '#FFFFFF';
        for (let k = 0; k < 9; k++) {
          const a = p.seed * 40 + k * 2.4;
          const d = 0.15 + 0.09 * k;
          ctx.globalAlpha = grow * (0.9 - d * 0.5);
          ctx.fillRect(Math.round(cx + Math.cos(a) * rx * d), Math.round(cy + Math.sin(a) * ry * d), k % 3 === 0 ? 3 : 2, k % 3 === 0 ? 1 : 2);
        }
      }
    }
    ctx.globalAlpha = 1;
  }

  /** Layer 7: drops, splashes and flakes in the air, over the bodies. */
  drawAir(ctx: Ctx, cam: Camera): void {
    if (this.rain > 0) {
      const n = this.activeRain();
      ctx.strokeStyle = '#BFE3FF';
      ctx.lineWidth = 1;
      ctx.globalAlpha = 0.55 * this.rain;
      ctx.beginPath();
      for (let i = 0; i < n; i++) {
        const d = this.drops[i]!;
        if (d.z <= 0) continue;
        const x = Math.round(cam.sx(d.x) - d.z * RAIN_SLANT), y = Math.round(cam.sy(d.y) - d.z);
        ctx.moveTo(x, y);
        ctx.lineTo(x - 3, y - 11);
      }
      ctx.stroke();
      ctx.fillStyle = '#BFE3FF';
      for (const sp of this.splashes) {
        if (sp.t >= SPLASH_S) continue;
        const f = sp.t / SPLASH_S;
        const x = Math.round(cam.sx(sp.x)), y = Math.round(cam.sy(sp.y));
        ctx.globalAlpha = (1 - f) * 0.7 * this.rain;
        // two droplets thrown out and a tiny ring
        ctx.fillRect(x - 2 - Math.round(f * 4), y - Math.round(f * 5), 2, 2);
        ctx.fillRect(x + 1 + Math.round(f * 4), y - Math.round(f * 4), 2, 2);
        ctx.fillRect(x - 3 - Math.round(f * 3), y, 6 + Math.round(f * 6), 1);
      }
    }
    if (this.snow > 0) {
      const n = this.activeSnow();
      ctx.fillStyle = '#FFFFFF';
      for (let i = 0; i < n; i++) {
        const f = this.flakes[i]!;
        const drift = f.z > 0 ? Math.sin(this.time * 1.3 + f.ph) * 6 - f.z * SNOW_SLANT : 0;
        const x = Math.round(cam.sx(f.x) + drift), y = Math.round(cam.sy(f.y) - f.z);
        ctx.globalAlpha = (f.z > 0 ? 0.85 : 0.6 * (f.settle / SETTLE_S)) * this.snow;
        const size = f.ph > 4.4 ? 3 : 2;
        ctx.fillRect(x, y, size, size);
      }
    }
    ctx.globalAlpha = 1;
  }

  /** Screen space, under the HUD: a light tint, and frosted edges in a blizzard. */
  drawScreen(ctx: Ctx, w: number, h: number): void {
    if (this.rain > 0) {
      ctx.fillStyle = `rgba(24,48,96,${(0.16 * this.rain).toFixed(3)})`;
      ctx.fillRect(0, 0, w, h);
    }
    if (this.snow > 0) {
      ctx.fillStyle = `rgba(214,234,255,${(0.1 * this.snow).toFixed(3)})`;
      ctx.fillRect(0, 0, w, h);
      const key = `${w}x${h}`;
      if (key !== this.edgeKey || !this.edge) {
        this.edgeKey = key;
        const g = ctx.createRadialGradient(w / 2, h / 2, Math.min(w, h) * 0.35, w / 2, h / 2, Math.hypot(w, h) * 0.55);
        g.addColorStop(0, 'rgba(235,246,255,0)');
        g.addColorStop(1, 'rgba(235,246,255,0.45)');
        this.edge = g;
      }
      ctx.globalAlpha = this.snow;
      ctx.fillStyle = this.edge;
      ctx.fillRect(0, 0, w, h);
      ctx.globalAlpha = 1;
    }
  }
}
